import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { selectUserGuid } from 'slices/commonSelectors';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { useHwcgFlag } from 'utils/featureFlags';
import { requestNeedsMyApproval } from 'components/IAP/ResourceRequests/ics213rr/ics213rrApprovalFilter';
import {
  useCreateResourceRequest,
  useGroupResourceRequests,
  useIncidentResourceRequests,
  type ResourceRequest,
  type ResourceRequestItem,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import { resourceRequestStatusLabel } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrVersioning';
import { itemDescription } from 'components/IAP/ResourceRequests/ics213rr/ics213rrPriority';
import { filterRequestsByWorkspace } from 'components/MapPage/CorporateViewTabs/ResourceTab/resourceRequestWorkspaceFilter';
import AssignmentListFilters, {
  type FilterColumn,
} from 'components/IAP/planningView/widgets/AssignmentList/AssignmentListFilters';
import { Ics213RrListTable } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrListTable';
import type { Ics213RrListRowData } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrListRow';

const BLANK_OPTION = '(Blank)';

const isBlankValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim().length === 0);

const collectSearchableText = (value: unknown, parts: string[]): void => {
  if (value === null || value === undefined || typeof value === 'boolean') {
    return;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) parts.push(trimmed);
    return;
  }
  if (typeof value === 'number') {
    parts.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      collectSearchableText(entry, parts);
    }
    return;
  }
  if (typeof value === 'object') {
    for (const nested of Object.values(value as Record<string, unknown>)) {
      collectSearchableText(nested, parts);
    }
  }
};

function createdRequestId(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const row = data as Record<string, unknown>;
  const id = row.resourceRequestId ?? row.resource_request_id;
  return typeof id === 'string' ? id : '';
}

function resourcesSummary(items: ResourceRequestItem[] | undefined): string {
  if (!items?.length) return '0 resources';
  if (items.length === 1) {
    const item = items[0];
    const kind = item.kind || '—';
    const type = item.type || '—';
    return `${kind} / ${type} × ${item.quantity ?? 0}`;
  }
  return `${items.length} resources`;
}

function toRowData(request: ResourceRequest): Ics213RrListRowData {
  return {
    ...request,
    statusLabel: resourceRequestStatusLabel(request.status),
    resourcesSummary: resourcesSummary(request.items),
    workspaceName: request.incident_name,
  };
}

function matchesKeywordSearch(row: Ics213RrListRowData, query: string) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  const parts: string[] = [];
  collectSearchableText(
    {
      name: row.name,
      request_number: row.request_number,
      requestor_name: row.requestor_name,
      statusLabel: row.statusLabel,
      resourcesSummary: row.resourcesSummary,
      workspaceName: row.workspaceName,
      items: (row.items ?? []).map((item) => ({
        kind: item.kind,
        type: item.type,
        quantity: item.quantity,
        description: itemDescription(item),
        name: item.name,
      })),
    },
    parts
  );
  return parts.join(' ').toLowerCase().includes(normalized);
}

type FilterFieldKey = 'status' | 'submittedBy' | 'name';

const FILTER_COLUMN_DEFS: { key: FilterFieldKey; label: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'submittedBy', label: 'Submitted by' },
  { key: 'name', label: 'Name' },
];

function filterFieldValue(
  row: Ics213RrListRowData,
  key: FilterFieldKey
): string {
  switch (key) {
    case 'status':
      return row.statusLabel;
    case 'submittedBy':
      return row.requestor_name ?? '';
    case 'name':
      return row.name ?? '';
    default:
      return '';
  }
}

export type Ics213RrListProps = {
  /** Group-wide list; view-only (hub). */
  hub?: boolean;
  readOnly?: boolean;
  /** Hub workspace filter (incident ids). Empty = all. */
  workspaceIds?: string[];
  /** Show the Add control. Defaults to !hub && !readOnly. */
  showAdd?: boolean;
};

/**
 * Shared searchable / filterable expandable 213RR table for Forms and Assets.
 * Add creates an unsigned draft and expands the new row.
 */
export function Ics213RrList({
  hub = false,
  readOnly: readOnlyProp,
  workspaceIds = [],
  showAdd: showAddProp,
}: Ics213RrListProps) {
  const readOnly = readOnlyProp ?? hub;
  const showAdd = showAddProp ?? (!hub && !readOnly);

  const userGuid = useSelector(selectUserGuid);
  const { data: roster } = useGetRosterForIncident();
  const hwcg = useHwcgFlag();
  const memberPositions =
    roster?.find((member) => member.userId === userGuid)?.incidentPositions ||
    [];

  const incidentQuery = useIncidentResourceRequests(false, { enabled: !hub });
  const groupQuery = useGroupResourceRequests({ enabled: hub });
  const {
    data: requests,
    isLoading,
    isError,
  } = hub ? groupQuery : incidentQuery;

  const createResourceRequest = useCreateResourceRequest();
  const [searchParams, setSearchParams] = useSearchParams();
  const openRequestId = searchParams.get('openRequest');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [scrollToId, setScrollToId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [needsMyApproval, setNeedsMyApproval] = useState(false);
  const showNeedsApproval = hwcg && !hub;
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >(() =>
    Object.fromEntries(FILTER_COLUMN_DEFS.map((column) => [column.key, []]))
  );

  useEffect(() => {
    if (!openRequestId || !requests) return;
    const match = requests.find(
      (request) =>
        request.resource_request_id === openRequestId ||
        request.id === openRequestId
    );
    const requestId = match?.resource_request_id ?? openRequestId;
    setExpandedId(requestId);
    setScrollToId(requestId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openRequest');
        return next;
      },
      { replace: true }
    );
  }, [openRequestId, requests, setSearchParams]);

  const workspaceFiltered = useMemo(() => {
    const list = (requests ?? []).filter((request) => !request.archived);
    return filterRequestsByWorkspace(
      list.map((request) => ({
        ...request,
        incidentId: request.incident_id,
      })),
      hub ? workspaceIds : []
    );
  }, [requests, hub, workspaceIds]);

  const rows = useMemo(
    () => workspaceFiltered.map(toRowData),
    [workspaceFiltered]
  );

  const filterColumns: FilterColumn[] = useMemo(() => {
    return FILTER_COLUMN_DEFS.map(({ key, label }) => {
      const values = rows.map((row) => filterFieldValue(row, key));
      const options = Array.from(
        new Set(
          values.filter(
            (value): value is string =>
              typeof value === 'string' && value.trim().length > 0
          )
        )
      ).sort((a, b) => a.localeCompare(b));
      return {
        key,
        label,
        options: values.some(isBlankValue)
          ? [...options, BLANK_OPTION]
          : options,
      };
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesColumns = FILTER_COLUMN_DEFS.every(({ key }) => {
        const picked = selectedFilters[key];
        if (!picked?.length) return true;
        const value = filterFieldValue(row, key);
        return isBlankValue(value)
          ? picked.includes(BLANK_OPTION)
          : picked.includes(String(value));
      });
      const matchesApproval =
        !showNeedsApproval ||
        !needsMyApproval ||
        requestNeedsMyApproval(row, memberPositions);
      return (
        matchesColumns &&
        matchesApproval &&
        matchesKeywordSearch(row, searchQuery)
      );
    });
  }, [
    rows,
    selectedFilters,
    searchQuery,
    showNeedsApproval,
    needsMyApproval,
    memberPositions,
  ]);

  const reqCount = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredRows.forEach((request) => {
      if (request.status === 'completed') return;
      request.logistics_items?.flat().forEach((item) => {
        if (item.resource_set_id) {
          counts[item.resource_set_id] =
            (counts[item.resource_set_id] || 0) + 1;
        }
      });
    });
    return counts;
  }, [filteredRows]);

  const toggleFilter = (columnKey: string, value: string) => {
    setSelectedFilters((prev) => {
      const picked = prev[columnKey] ?? [];
      return {
        ...prev,
        [columnKey]: picked.includes(value)
          ? picked.filter((entry) => entry !== value)
          : [...picked, value],
      };
    });
  };

  const clearFilters = () => {
    setSelectedFilters(
      Object.fromEntries(FILTER_COLUMN_DEFS.map((column) => [column.key, []]))
    );
  };

  const handleAdd = async () => {
    if (createResourceRequest.isPending) return;
    try {
      const created = await createResourceRequest.mutateAsync({
        name: 'Resource Request',
        items: [],
        status: 'draft',
      });
      const requestId = createdRequestId(created);
      if (requestId) {
        setExpandedId(requestId);
        setScrollToId(requestId);
      }
    } catch {
      // Toast handled by mutation onError.
    }
  };

  const onToggleRow = (requestId: string) => {
    setExpandedId((prev) => (prev === requestId ? null : requestId));
    setScrollToId(null);
  };

  if (isLoading) {
    return (
      <p className="tw:text-sm tw:text-muted-foreground">Loading requests…</p>
    );
  }
  if (isError) {
    return (
      <p className="tw:text-sm tw:text-destructive">Failed to load items.</p>
    );
  }

  return (
    <div
      className="tw:flex tw:flex-col tw:gap-2"
      data-testid="ics213rr-list"
      data-tour="resources-requests-list"
    >
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {showAdd ? (
          <Button
            type="button"
            size="sm"
            data-testid="ics213rr-add"
            data-tour="resources-request-create"
            disabled={createResourceRequest.isPending}
            onClick={() => void handleAdd()}
          >
            <Plus className="tw:size-4" />
            Add 213RR
          </Button>
        ) : null}
        {showNeedsApproval ? (
          <Button
            type="button"
            size="sm"
            variant={needsMyApproval ? 'default' : 'outline'}
            aria-pressed={needsMyApproval}
            data-testid="ics213rr-needs-my-approval"
            onClick={() => setNeedsMyApproval((current) => !current)}
          >
            Needs My Approval
          </Button>
        ) : null}
      </div>
      <AssignmentListFilters
        columns={filterColumns}
        selected={selectedFilters}
        onToggle={toggleFilter}
        onClearAll={clearFilters}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search resource requests…"
      />
      <Ics213RrListTable
        data={filteredRows}
        expandedId={expandedId}
        onToggleRow={onToggleRow}
        scrollToId={scrollToId}
        memberPositions={memberPositions}
        reqCount={reqCount}
        readOnly={readOnly}
      />
    </div>
  );
}
